import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock, User, Building, ArrowRight, AlertCircle } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'

export default function Register() {
  const navigate = useNavigate()
  const { register } = useAuth()
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company_name: '',
    password: '',
    confirm_password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  
  // Debounce timers for password validation
  const passwordValidationTimer = useRef<NodeJS.Timeout | null>(null)
  const confirmPasswordValidationTimer = useRef<NodeJS.Timeout | null>(null)
  
  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (passwordValidationTimer.current) {
        clearTimeout(passwordValidationTimer.current)
      }
      if (confirmPasswordValidationTimer.current) {
        clearTimeout(confirmPasswordValidationTimer.current)
      }
    }
  }, [])

  const validatePassword = (password: string): string | null => {
    if (!password) return 'Password is required'
    
    // Check all requirements and return a single comprehensive error message
    const requirements: string[] = []
    if (password.length < 8) requirements.push('at least 8 characters')
    if (!/[A-Z]/.test(password)) requirements.push('1 uppercase letter')
    if (!/[a-z]/.test(password)) requirements.push('1 lowercase letter')
    if (!/[0-9]/.test(password)) requirements.push('1 number')
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) requirements.push('1 special character')
    
    if (requirements.length > 0) {
      return `Password must contain ${requirements.join(', ')}`
    }
    
    return null
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    // Clear error for this field when user starts typing (immediate for non-password fields)
    if (errors[name] && name !== 'password' && name !== 'confirm_password') {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
    
    // Debounced password validation (1.5 second delay)
    if (name === 'password') {
      // Clear existing timer
      if (passwordValidationTimer.current) {
        clearTimeout(passwordValidationTimer.current)
      }
      
      // Clear error immediately when user starts typing
      if (errors.password) {
        setErrors(prev => {
          const newErrors = { ...prev }
          delete newErrors.password
          return newErrors
        })
      }
      
      // Set new timer to validate after 1.5 seconds of no typing
      // Capture the current value and confirm_password for the closure
      const currentPasswordValue = value
      passwordValidationTimer.current = setTimeout(() => {
        // Check if password value is still the same (user hasn't continued typing)
        setFormData(currentFormData => {
          if (currentFormData.password === currentPasswordValue) {
            if (currentPasswordValue) {
              const passwordError = validatePassword(currentPasswordValue)
              if (passwordError) {
                setErrors(prev => ({ ...prev, password: passwordError }))
              } else {
                setErrors(prev => {
                  const newErrors = { ...prev }
                  delete newErrors.password
                  return newErrors
                })
              }
              
              // Also check confirm password match if confirm_password is filled
              if (currentFormData.confirm_password) {
                if (currentPasswordValue !== currentFormData.confirm_password) {
                  setErrors(prev => ({ ...prev, confirm_password: 'Passwords do not match' }))
                } else {
                  setErrors(prev => {
                    const newErrors = { ...prev }
                    delete newErrors.confirm_password
                    return newErrors
                  })
                }
              }
            }
          }
          return currentFormData // Return unchanged
        })
      }, 1500) // 1.5 second delay
    }
    
    // Debounced confirm password validation (1.5 second delay)
    if (name === 'confirm_password') {
      // Clear existing timer
      if (confirmPasswordValidationTimer.current) {
        clearTimeout(confirmPasswordValidationTimer.current)
      }
      
      // Clear error immediately when user starts typing
      if (errors.confirm_password) {
        setErrors(prev => {
          const newErrors = { ...prev }
          delete newErrors.confirm_password
          return newErrors
        })
      }
      
      // Set new timer to validate after 1.5 seconds of no typing
      // Capture the current value for the closure
      const currentConfirmPasswordValue = value
      confirmPasswordValidationTimer.current = setTimeout(() => {
        // Check if confirm_password value is still the same (user hasn't continued typing)
        setFormData(currentFormData => {
          if (currentFormData.confirm_password === currentConfirmPasswordValue) {
            if (currentConfirmPasswordValue) {
              if (currentConfirmPasswordValue !== currentFormData.password) {
                setErrors(prev => ({ ...prev, confirm_password: 'Passwords do not match' }))
              } else {
                setErrors(prev => {
                  const newErrors = { ...prev }
                  delete newErrors.confirm_password
                  return newErrors
                })
              }
            }
          }
          return currentFormData // Return unchanged
        })
      }, 1500) // 1.5 second delay
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please include an "@" in the email address'
    }

    // First name validation
    if (!formData.first_name) {
      newErrors.first_name = 'First name is required'
    } else if (formData.first_name.length < 2) {
      newErrors.first_name = 'First name must be at least 2 characters'
    }

    // Last name validation (optional per MVP, but validate if provided)
    if (formData.last_name && formData.last_name.length < 2) {
      newErrors.last_name = 'Last name must be at least 2 characters'
    }

    // Company name validation (optional per MVP, but validate if provided)
    if (formData.company_name && formData.company_name.length < 2) {
      newErrors.company_name = 'Company name must be at least 2 characters'
    }

    // Password validation with full requirements
    const passwordError = validatePassword(formData.password)
    if (passwordError) {
      newErrors.password = passwordError
    }

    // Confirm password validation
    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password'
    } else if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    setIsLoading(true)
    setErrors({})

    try {
      await register({
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        company_name: formData.company_name,
      })
          setIsSuccess(true)
      setTimeout(() => navigate('/alerts'), 1200)
    } catch (err: any) {
      // Map common server errors
      const message = (err as Error)?.message || ''
      if (message.includes('409') || message.toLowerCase().includes('exists') || message.toLowerCase().includes('duplicate')) {
        setErrors({ email: 'An account with this email already exists' })
      } else {
        setErrors({ general: 'Registration failed. Please try again.' })
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%)' }}>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="mb-6 text-center">
              <div className="flex justify-center items-center gap-2">
                <a href="https://gotandem.ai" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-90">
                  <img className="h-12 w-auto" src="/images/tandem-logo.svg" alt="Tandem" />
                  <span className="text-2xl font-bold text-gray-900">Tandem</span>
                </a>
              </div>
              <h2 className="mt-4 text-2xl font-extrabold text-gray-900">Account Created!</h2>
              <p className="mt-1 text-sm text-gray-600">Welcome to Tandem! Redirecting to your dashboard...</p>
            </div>
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-tandem-blue mx-auto"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%)' }}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="mb-6 text-center">
            <div className="flex justify-center items-center gap-2">
              <a href="https://gotandem.ai" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-90">
                <img className="h-12 w-auto" src="/images/tandem-logo.svg" alt="Tandem" />
                <span className="text-2xl font-bold text-gray-900">Tandem</span>
              </a>
            </div>
            <h2 className="mt-4 text-2xl font-extrabold text-gray-900">Create your account</h2>
            <p className="mt-1 text-sm text-gray-600">Join Tandem and discover revenue opportunities</p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm flex items-center">
                <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                {errors.general}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input id="email" name="email" type="email" autoComplete="email" required className={`input pl-10 ${errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`} placeholder="Enter your email" value={formData.email} onChange={handleChange} />
              </div>
              <p className="mt-1 text-sm text-gray-500">Used for login and notifications</p>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">First Name</label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input id="first_name" name="first_name" type="text" required className={`input pl-10 ${errors.first_name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`} placeholder="Enter your first name" value={formData.first_name} onChange={handleChange} />
              </div>
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.first_name}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">Last Name <span className="text-gray-400 text-xs">(optional)</span></label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input id="last_name" name="last_name" type="text" className={`input pl-10 ${errors.last_name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`} placeholder="Enter your last name" value={formData.last_name} onChange={handleChange} />
              </div>
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.last_name}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">Company Name <span className="text-gray-400 text-xs">(optional)</span></label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building className="h-5 w-5 text-gray-400" />
                </div>
                <input id="company_name" name="company_name" type="text" className={`input pl-10 ${errors.company_name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`} placeholder="Enter your company name" value={formData.company_name} onChange={handleChange} />
              </div>
              {errors.company_name && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.company_name}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
              <p className="mt-1 text-xs text-gray-500">At least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character</p>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required className={`input pl-10 pr-10 ${errors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`} placeholder="Create a password" value={formData.password} onChange={handleChange} />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.password}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="confirm_password" className="block text sm font-medium text-gray-700">Confirm Password</label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input id="confirm_password" name="confirm_password" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" required className={`input pl-10 pr-10 ${errors.confirm_password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`} placeholder="Confirm your password" value={formData.confirm_password} onChange={handleChange} />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                  )}
                </button>
              </div>
              {errors.confirm_password && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.confirm_password}
                </p>
              )}
            </div>

            <div>
              <button type="submit" disabled={isLoading} className="w-full btn-primary flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed">
                {isLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300" /></div>
              <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">Already have an account?</span></div>
            </div>
            <div className="mt-6">
              <Link to="/login" className="w-full btn-secondary block text-center">Sign in instead</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
